import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { financeApi } from '../../services/financeApi';
import { ProofDaySummary, ProofsListResponse, PROOF_STRIP_DAYS } from '../../types/proofs';
import ProofStrip from '../../components/finance/ProofStrip';
import ProofDetail from '../../components/finance/ProofDetail';
import { dubaiDate } from './PayoutsTab';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Spec 053 Flow E step 3 — Finance → Proofs. The last 31 days as a strip (3 rows × 31 days), a
 * drawer for the selected run's lhs / rhs / exceptions, and "Run proofs now" for a chosen date
 * (default yesterday; today takes a fresh Stripe snapshot first). The nightly job runs at 03:00
 * Asia/Dubai; an on-demand run replaces that date's rows, so the strip is refetched afterwards.
 */
export default function ProofsTab() {
  const auth = useAuth();
  const today = dubaiDate();
  const [data, setData] = useState<ProofsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runDate, setRunDate] = useState(() => dubaiDate(-1));
  const [running, setRunning] = useState(false);
  const [runNote, setRunNote] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!auth.token) return;
    setLoading(true);
    setError(null);
    try {
      setData(await financeApi.listProofs(auth.token, PROOF_STRIP_DAYS));
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : 'Failed to load proof runs');
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    if (!auth.token || running) return;
    setRunning(true);
    setRunNote(null);
    setRunError(null);
    try {
      const runs = await financeApi.runProofs(runDate, auth.token);
      const failed = runs.filter((r) => r.status === 'FAIL').length;
      const inconclusive = runs.filter((r) => r.status === 'INCONCLUSIVE').length;
      setRunNote(
        `Ran ${runs.length} proof${runs.length === 1 ? '' : 's'} for ${runDate}: `
        + `${runs.length - failed - inconclusive} passed, ${failed} failed, ${inconclusive} inconclusive.`,
      );
      await load();
    } catch (err) {
      // 400 = date in the future or before ledger go-live; the backend's message says which.
      setRunError(err instanceof Error ? `Run failed: ${err.message}` : 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const onSelect = useCallback((run: ProofDaySummary) => setSelectedRunId(run.runId), []);
  const closeDetail = useCallback(() => setSelectedRunId(null), []);

  return (
    <div>
      {runNote && <div className="admin-info-banner info" role="status" data-testid="run-note">{runNote}</div>}
      {runError && <div className="admin-info-banner danger" role="alert" data-testid="run-error">{runError}</div>}

      <div className="admin-card">
        <div className="admin-card-header" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h3 className="admin-card-title">Nightly proofs — last {PROOF_STRIP_DAYS} days</h3>
            {data && (
              <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
                Ledger live since {data.ledgerLiveSince} · runs at 03:00 Asia/Dubai for the prior day
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.8125rem' }} role="group" aria-label="Run proofs">
            <label htmlFor="proofs-run-date" style={{ color: 'var(--admin-text-muted)' }}>Date</label>
            <input id="proofs-run-date" type="date" className="admin-search-input" value={runDate}
              min={data?.ledgerLiveSince} max={today} disabled={running}
              onChange={(e) => { if (DATE_RE.test(e.target.value)) setRunDate(e.target.value); }} />
            <button type="button" className="admin-btn admin-btn-sm admin-btn-primary" disabled={running || loading} onClick={runNow}>
              {running ? 'Running…' : 'Run proofs now'}
            </button>
          </div>
        </div>

        {error && <div className="admin-info-banner danger" role="alert">{error}</div>}

        {loading && !data ? (
          <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
        ) : data && (
          <ProofStrip days={data.days} selectedRunId={selectedRunId} onSelect={onSelect} />
        )}
      </div>

      <ProofDetail runId={selectedRunId} onClose={closeDetail} />
    </div>
  );
}
